<template>
  <div class="settings-panel">
    <button class="settings-toggle" @click="open = !open">
      {{ open ? "▲ Settings" : "▼ Settings" }}
    </button>
    <div v-if="open" class="settings-body">
      <!-- Trick Level — Set All (SET-01 repurposed as bulk convenience) -->
      <section class="settings-section">
        <h3 class="settings-heading">Trick Level — Set All</h3>
        <span class="settings-helper">Applies to every trick below</span>
        <select
          id="trickLevel"
          class="settings-select"
          :value="settings.trickLevel"
          @change="setAllTricks($event.target.value)"
        >
          <option v-for="(label, idx) in TRICK_LABELS" :key="idx" :value="idx">
            {{ label }}
          </option>
        </select>
      </section>

      <!-- Per-Trick Difficulty (SET-06) -->
      <section class="settings-section">
        <h3 class="settings-heading">Per-Trick Difficulty</h3>
        <div class="trick-scroll">
          <div
            v-for="key in trickKeys"
            :key="key"
            class="settings-row trick-row"
          >
            <label class="settings-label trick-label">{{
              trickLongName(key)
            }}</label>
            <select
              class="settings-select trick-select"
              :value="
                settings.trickLevels[key] != null
                  ? settings.trickLevels[key]
                  : 0
              "
              @change="setTrickLevelForKey(key, $event.target.value)"
            >
              <option v-for="lvl in trickOptions(key)" :key="lvl" :value="lvl">
                {{ TRICK_LABELS[lvl] }}
              </option>
            </select>
          </div>
        </div>
      </section>

      <!-- Misc Toggles (SET-02) -->
      <section class="settings-section">
        <h3 class="settings-heading">Misc Toggles</h3>
        <div v-for="flag in miscFlags" :key="flag.key" class="settings-row">
          <label class="settings-label">
            <input
              type="checkbox"
              class="settings-checkbox"
              :checked="settings.misc[flag.key] === 1"
              @change="setMisc(flag.key, $event.target.checked)"
            />
            {{ flag.label }}
          </label>
        </div>
      </section>

      <!-- Starting Inventory (SET-03) -->
      <section class="settings-section">
        <h3 class="settings-heading">Starting Inventory</h3>
        <div class="settings-row">
          <label class="settings-label" for="startMissiles"
            >Starting Missiles</label
          >
          <input
            id="startMissiles"
            type="number"
            class="settings-number"
            min="0"
            :value="settings.startingItems.MissileAmmo"
            @change="setStartingItem('MissileAmmo', $event.target.value)"
          />
        </div>
        <div class="settings-row">
          <label class="settings-label">
            <input
              type="checkbox"
              class="settings-checkbox"
              :checked="settings.startingItems.Slide === 1"
              @change="setStartingItem('Slide', $event.target.checked ? 1 : 0)"
            />
            Slide
          </label>
        </div>
        <div class="settings-row">
          <label class="settings-label">
            <input
              type="checkbox"
              class="settings-checkbox"
              :checked="settings.startingItems.Pulse === 1"
              @change="setStartingItem('Pulse', $event.target.checked ? 1 : 0)"
            />
            Pulse Radar
          </label>
        </div>
        <div class="settings-row">
          <label class="settings-label" for="startEnergyTanks"
            >Energy Tanks</label
          >
          <input
            id="startEnergyTanks"
            type="number"
            class="settings-number"
            min="0"
            :value="settings.startingItems.ETank || 0"
            @change="setStartingItem('ETank', $event.target.value)"
          />
        </div>
      </section>

      <!-- Energy Params (SET-03, advanced) — no energy_per_tank control (W3) -->
      <section class="settings-section">
        <h3 class="settings-heading">Energy Params</h3>
        <div class="settings-row">
          <label class="settings-label">
            <input
              type="checkbox"
              class="settings-checkbox"
              :checked="settings.immediateParts"
              @change="setImmediateParts($event.target.checked)"
            />
            Immediate Parts
          </label>
        </div>
        <div class="settings-row">
          <label class="settings-label" for="strictness"
            >Damage Strictness</label
          >
          <select
            id="strictness"
            class="settings-select"
            :value="settings.strictness"
            @change="setStrictness($event.target.value)"
          >
            <option value="1">1.0 — Lax</option>
            <option value="1.5">1.5 — Normal</option>
            <option value="2">2.0 — Strict</option>
          </select>
        </div>
      </section>

      <!-- Required DNA (LOG-01/LOG-02) -->
      <section class="settings-section">
        <h3 class="settings-heading">Required DNA</h3>
        <div class="settings-row">
          <label class="settings-label" for="requiredDna">Required DNA</label>
          <input
            id="requiredDna"
            type="number"
            class="settings-number"
            min="1"
            max="12"
            :value="settings.requiredDna != null ? settings.requiredDna : 12"
            @change="setRequiredDna($event.target.value)"
          />
        </div>
      </section>

      <!-- Known Limitations (SET-07) -->
      <section class="settings-section">
        <h3 class="settings-heading">Known Limitations</h3>
        <p class="settings-limitations">
          Not modeled: teleporter/door target remapping, custom energy-per-tank.
          Reachability may differ from Randovania for seeds using these.
        </p>
      </section>

      <!-- Reset Progress -->
      <section class="settings-section">
        <button class="settings-reset" @click="resetProgress">
          Reset Progress
        </button>
      </section>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";

const MISC_FLAGS = [
  { key: "DoorLocks", label: "Door Locks" },
  { key: "HighDanger", label: "High Danger" },
  { key: "NerfPowerBombs", label: "Nerf Power Bombs" },
  { key: "Teleporters", label: "Teleporters" },
  { key: "SeparateBeams", label: "Separate Beams" },
  { key: "SeparateMissiles", label: "Separate Missiles" },
];

const TRICK_LABELS = [
  "0 — Disabled",
  "1 — Beginner",
  "2 — Intermediate",
  "3 — Advanced",
  "4 — Expert",
  "5 — Ludicrous",
];

// Fallback settings shape for before the GameModel has loaded.
const FALLBACK_SETTINGS = {
  trickLevel: 0,
  trickLevels: {},
  requiredDna: 12,
  misc: {
    SeparateBeams: 0,
    SeparateMissiles: 0,
    DoorLocks: 0,
    HighDanger: 0,
    Teleporters: 0,
    NerfPowerBombs: 1,
  },
  energyPerTank: 100,
  immediateParts: true,
  strictness: 1.5,
  startingItems: {
    MissileLauncher: 1,
    MissileAmmo: 15,
    Pulse: 1,
    Slide: 1,
  },
};

export default {
  name: "SettingsPanel",

  data() {
    return {
      open: false,
      miscFlags: MISC_FLAGS,
      // Expose to template (needed for v-for over labels in selects)
      TRICK_LABELS,
    };
  },

  computed: {
    ...mapState("logic", {
      rawSettings: (state) => state.settings,
      rawGameModel: (state) => state.gameModel,
    }),

    /** Per-trick max level map from logic/trickMaxes getter (Plan 02). */
    trickMaxes() {
      return this.$store.getters["logic/trickMaxes"] || {};
    },

    /** Guard: fall back to defaults when settings is null (pre-load). */
    settings() {
      if (!this.rawSettings) return FALLBACK_SETTINGS;
      return {
        ...FALLBACK_SETTINGS,
        ...this.rawSettings,
        trickLevels: {
          ...FALLBACK_SETTINGS.trickLevels,
          ...(this.rawSettings.trickLevels || {}),
        },
        misc: { ...FALLBACK_SETTINGS.misc, ...(this.rawSettings.misc || {}) },
        startingItems: {
          ...FALLBACK_SETTINGS.startingItems,
          ...(this.rawSettings.startingItems || {}),
        },
      };
    },

    /**
     * Ordered list of trick keys sourced from trickMaxes (which was built from
     * header.used_trick_levels). Falls back to gameModel.rdb.tricks keys if a
     * trick appears in the rdb but has no used-level entry (defensive).
     */
    trickKeys() {
      const maxKeys = Object.keys(this.trickMaxes);
      if (maxKeys.length > 0) return maxKeys;
      // Fallback: use gameModel rdb tricks keys if trickMaxes not yet populated
      if (
        this.rawGameModel &&
        this.rawGameModel.rdb &&
        this.rawGameModel.rdb.tricks
      ) {
        return Object.keys(this.rawGameModel.rdb.tricks);
      }
      return [];
    },
  },

  methods: {
    /**
     * Returns the long_name for a trick key from the loaded gameModel.
     * Falls back to the key itself if the model is not yet available.
     */
    trickLongName(key) {
      if (
        this.rawGameModel &&
        this.rawGameModel.rdb &&
        this.rawGameModel.rdb.tricks &&
        this.rawGameModel.rdb.tricks[key]
      ) {
        return this.rawGameModel.rdb.tricks[key].long_name || key;
      }
      return key;
    },

    /**
     * Returns an array of numeric option values (0 .. trickMax[key]) for a
     * given trick key. Options beyond a trick's max must not render (AC-2).
     */
    trickOptions(key) {
      const max = this.trickMaxes[key] != null ? this.trickMaxes[key] : 0;
      const opts = [];
      for (let i = 0; i <= max; i++) opts.push(i);
      return opts;
    },

    /**
     * Dispatch a per-trick level change. Each change patches only the single
     * trick key; SET_SETTINGS deep-merges so siblings are preserved (Pattern 2).
     */
    setTrickLevelForKey(key, value) {
      this.$store.dispatch("logic/setSettings", {
        trickLevels: { [key]: Number(value) },
      });
    },

    /**
     * Bulk "Set all tricks" convenience (repurposed global control).
     * Clamps each trick to its own max so a trick that caps at Beginner never
     * gets set to Advanced. Dispatches a single setSettings with the full patch.
     */
    setAllTricks(value) {
      const level = Number(value);
      const patch = {};
      for (const key of this.trickKeys) {
        const max = this.trickMaxes[key] != null ? this.trickMaxes[key] : 0;
        patch[key] = Math.min(level, max);
      }
      this.$store.dispatch("logic/setSettings", {
        trickLevel: level,
        trickLevels: patch,
      });
    },

    setMisc(flag, checked) {
      this.$store.dispatch("logic/setSettings", {
        misc: { [flag]: checked ? 1 : 0 },
      });
    },

    setStartingItem(key, value) {
      const numericKeys = ["MissileAmmo", "ETank"];
      this.$store.dispatch("logic/setSettings", {
        startingItems: {
          [key]: numericKeys.includes(key) ? Number(value) || 0 : value,
        },
      });
    },

    setImmediateParts(checked) {
      this.$store.dispatch("logic/setSettings", {
        immediateParts: checked,
      });
    },

    setStrictness(value) {
      this.$store.dispatch("logic/setSettings", {
        strictness: Number(value),
      });
    },

    /**
     * Set required DNA count, clamped to [1,12] (T-07-07 / AC-7).
     * Belt-and-suspenders: the store also clamps on SET_SETTINGS.
     */
    setRequiredDna(value) {
      const clamped = Math.min(12, Math.max(1, Math.round(Number(value)) || 1));
      this.$store.dispatch("logic/setSettings", { requiredDna: clamped });
    },

    resetProgress() {
      if (window.confirm("Reset all progress? This cannot be undone.")) {
        this.$store.dispatch("resetProgress");
      }
    },
  },
};
</script>

<style scoped>
.settings-panel {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 100;
  font-family: Avenir, Helvetica, Arial, sans-serif;
  font-size: 12px;
  color: #92a5b8;
}

.settings-toggle {
  display: block;
  background: #0b131a;
  color: #92a5b8;
  border: 1px solid #92a5b8;
  padding: 4px 10px;
  cursor: pointer;
  font-size: 12px;
  width: 100%;
  text-align: right;
}

.settings-body {
  background: #0b131a;
  border: 1px solid #92a5b8;
  border-top: none;
  padding: 8px 12px;
  width: 220px;
  max-height: 80vh;
  overflow-y: auto;
}

.settings-section {
  margin-bottom: 12px;
}

.settings-heading {
  color: #92a5b8;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 6px 0;
  border-bottom: 1px solid #1e2d3a;
  padding-bottom: 2px;
}

.settings-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  color: #92a5b8;
}

.settings-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.settings-select {
  background: #0b131a;
  color: #92a5b8;
  border: 1px solid #92a5b8;
  padding: 2px 4px;
  font-size: 11px;
  width: 100%;
  margin-top: 4px;
}

.settings-number {
  background: #0b131a;
  color: #92a5b8;
  border: 1px solid #92a5b8;
  padding: 2px 4px;
  font-size: 11px;
  width: 60px;
  text-align: right;
}

.settings-checkbox {
  display: inline-block;
  width: auto;
  cursor: pointer;
  accent-color: #42b983;
}

.settings-reset {
  background: #1a0b0b;
  color: #e05555;
  border: 1px solid #e05555;
  padding: 4px 10px;
  cursor: pointer;
  font-size: 11px;
  width: 100%;
}

.settings-reset:hover {
  background: #2a1010;
}

/* Helper line beneath a section heading (e.g. "Applies to every trick below") */
.settings-helper {
  display: block;
  color: #92a5b8;
  font-size: 10px;
  margin-bottom: 4px;
  opacity: 0.75;
}

/* Scrollable container for ~26 per-trick rows (UI-SPEC §Spacing Scale) */
.trick-scroll {
  max-height: 240px;
  overflow-y: auto;
}

/* Per-trick row: name on left, select on right */
.trick-row {
  align-items: center;
  margin-bottom: 4px;
}

/* Trick long-name label — flex-shrink so it doesn't overflow */
.trick-label {
  flex: 1 1 auto;
  font-size: 11px;
  cursor: default;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 4px;
}

/* Per-trick select — narrower than the full-width global select */
.trick-select {
  flex: 0 0 auto;
  width: auto;
  min-width: 100px;
  margin-top: 0;
}

/* Known-limitations muted note (UI-SPEC §Typography — 10px muted) */
.settings-limitations {
  color: #92a5b8;
  font-size: 10px;
  line-height: 1.4;
  margin: 0;
  opacity: 0.75;
}
</style>
